import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Science as ScienceIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import AISidebar from './AISidebar';
import { companyApi } from '../../services/api';

const sectorColors = {
  Pharmaceutical: '#1976d2',
  Biotechnology: '#7b1fa2',
  'Medical Devices': '#00796b',
  Diagnostic: '#ff5722',
  default: '#757575',
};

const SIDEBAR_WIDTH = 320;

function CompanyList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // Sidebar state (for mobile)
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sector, setSector] = useState(searchParams.get('sector') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Stats
  const [stats, setStats] = useState({
    totalCompanies: 0,
    recentEvents: 0,
    activeDrugs: 0,
  });

  useEffect(() => {
    loadCompanies();
  }, [page, rowsPerPage]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (search) params.search = search;
      if (sector) params.sector = sector;

      const response = await companyApi.getCompanies(params);
      setCompanies(response.data.companies || []);
      setTotal(response.data.count || 0);

      // Update URL params
      const newParams = new URLSearchParams();
      if (page > 0) newParams.set('page', page);
      if (search) newParams.set('search', search);
      if (sector) newParams.set('sector', sector);
      setSearchParams(newParams);
    } catch (err) {
      setError('Failed to load companies. Please check the API connection.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadCompanies();
  };

  const clearFilters = () => {
    setSearch('');
    setSector('');
    setPage(0);
  };

  const formatMarketCap = (value) => {
    if (!value) return '-';
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const getSectorColor = (sectorName) => {
    if (!sectorName) return sectorColors.default;
    for (const [key, color] of Object.entries(sectorColors)) {
      if (sectorName.toLowerCase().includes(key.toLowerCase())) {
        return color;
      }
    }
    return sectorColors.default;
  };

  // AI Sidebar content
  const sidebarContent = (
    <AISidebar />  // No companyId = global mode
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)' }}>
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          pr: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Company Intelligence
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track pharmaceutical companies, their drug pipelines, SEC filings, and clinical trials.
          </Typography>
        </Box>
        {/* Mobile AI Button */}
        {isMobile && (
          <IconButton
            color="primary"
            onClick={() => setSidebarOpen(true)}
            sx={{ bgcolor: 'primary.light', color: 'white' }}
          >
            <ChatIcon />
          </IconButton>
        )}
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">{total}</Typography>
                <Typography variant="body2" color="text.secondary">Tracked Companies</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">-</Typography>
                <Typography variant="body2" color="text.secondary">Events (30 days)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ScienceIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">-</Typography>
                <Typography variant="body2" color="text.secondary">Active Drug Programs</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by company or ticker..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sector</InputLabel>
            <Select
              value={sector}
              label="Sector"
              onChange={(e) => setSector(e.target.value)}
            >
              <MenuItem value="">All Sectors</MenuItem>
              <MenuItem value="Pharmaceutical">Pharmaceutical</MenuItem>
              <MenuItem value="Biotechnology">Biotechnology</MenuItem>
              <MenuItem value="Medical Devices">Medical Devices</MenuItem>
              <MenuItem value="Diagnostic">Diagnostic</MenuItem>
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" size="small">
            Search
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={clearFilters}>
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Results */}
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Sector</TableCell>
                  <TableCell>Exchange</TableCell>
                  <TableCell align="right">Market Cap</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No companies found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow
                      key={company.company_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/company/${company.company_id}`)}
                    >
                      <TableCell>
                        <Typography fontWeight="bold" color="primary">
                          {company.ticker || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{company.company_name}</TableCell>
                      <TableCell>
                        {company.sector && (
                          <Chip
                            label={company.sector.split(' ')[0]}
                            size="small"
                            sx={{
                              bgcolor: getSectorColor(company.sector),
                              color: 'white',
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {company.exchange || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatMarketCap(company.market_cap)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
      </Box>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: 64,
            width: SIDEBAR_WIDTH,
            height: 'calc(100vh - 64px)',
            zIndex: 1000,
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Mobile Sidebar Drawer */}
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
          sx: { width: SIDEBAR_WIDTH },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setSidebarOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {sidebarContent}
      </Drawer>
    </Box>
  );
}

export default CompanyList;
